import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
import AuthLocaleBar, {
  authSiblingPath,
  normalizeLocale,
  ProductLocale,
} from "../../Components/Common/AuthLocaleBar";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getBrandLogos, isIntlPortalHost } from "../../helpers/brandPortal";
import { postJwtRegisterAccount, postJwtGoogleAuth } from "../../helpers/fakebackend_helper";
import { persistTenantSession } from "../../helpers/api_helper";
import { fetchPublicTrialActive } from "../../helpers/public_plans_helper";
import { loginSuccess } from "../../slices/auth/login/reducer";
import GoogleSignInButton from "../../Components/Common/GoogleSignInButton";

const LOCALES = ["pt-BR", "en", "es"] as const;

/** Mensagem amigável — sem error.code cru na UI (DOC 13). */
const resolveRegisterError = (
  err: any,
  t: (key: string, opts?: Record<string, string>) => string
): string => {
  if (err?.status === 429 || err?.code === "RATE_LIMIT_EXCEEDED") {
    return t("auth.register.error.rateLimited");
  }
  if (
    err?.code === "TRIAL_PLAN_UNAVAILABLE" ||
    err?.code === "REGISTER_PLAN_UNAVAILABLE"
  ) {
    return t("auth.register.error.planUnavailable");
  }
  // E-mail duplicado vem em details.email (VALIDATION_ERROR).
  const emailDetail = err?.details?.email?.[0];
  if (emailDetail) {
    return emailDetail;
  }
  if (err?.code === "VALIDATION_ERROR" && err?.message) {
    return err.message;
  }
  return err?.message || t("auth.register.error.generic");
};

/**
 * Signup self-serve — POST /api/v1/register (LAC-048).
 * Se o plano Trial estiver no catálogo público → copy de teste; senão o produto
 * trata Trial como inexistente (sem dizer “desativado”) e segue Assinatura/checkout.
 */
const Register = (props: any) => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmShow, setConfirmShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTone, setErrorTone] = useState<"danger" | "warning">("danger");
  // null = carregando — skeleton (não promete Trial; também não anuncia ausência).
  const [trialActive, setTrialActive] = useState<boolean | null>(null);
  const referralCode = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("ref");
    return value?.trim().toUpperCase() || "";
  }, []);

  // Porta brsend ou ?market=intl → preferência USD (checkout PayPal ainda F2).
  // Mercado ≠ idioma: ?lang= / i18n mandam no locale da UI.
  const intlMarket = useMemo(() => {
    const market = new URLSearchParams(window.location.search).get("market");
    return isIntlPortalHost() || market === "intl";
  }, []);

  const defaultLocale = useMemo(() => {
    const fromQuery = normalizeLocale(
      new URLSearchParams(window.location.search).get("lang")
    );
    if (fromQuery) return fromQuery;
    const lang = normalizeLocale(i18n.language);
    if (lang) return lang;
    return intlMarket ? "en" : "pt-BR";
  }, [i18n.language, intlMarket]);

  const preferredBillingCurrency = intlMarket ? "USD" : "BRL";

  useEffect(() => {
    let cancelled = false;
    fetchPublicTrialActive("BRL").then((active) => {
      if (!cancelled) setTrialActive(active);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      email: "",
      company_name: "",
      password: "",
      confirm_password: "",
      locale: defaultLocale,
      accept_terms: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("auth.register.validation.nameRequired")),
      email: Yup.string()
        .email(t("auth.register.validation.emailInvalid"))
        .required(t("auth.register.validation.emailRequired")),
      company_name: Yup.string().required(
        t("auth.register.validation.companyRequired")
      ),
      password: Yup.string()
        .min(8, t("auth.register.validation.passwordMin"))
        .required(t("auth.register.validation.passwordRequired")),
      confirm_password: Yup.string()
        .oneOf(
          [Yup.ref("password")],
          t("auth.register.validation.passwordMismatch")
        )
        .required(t("auth.register.validation.confirmRequired")),
      locale: Yup.string().oneOf([...LOCALES]),
      // Checkbox obrigatório — links abrem o site público (DOC 21).
      accept_terms: Yup.boolean().oneOf(
        [true],
        t("auth.register.validation.acceptTerms")
      ),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setErrorMessage(null);
      setErrorTone("danger");
      try {
        const envelope: any = await postJwtRegisterAccount({
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password,
          company_name: values.company_name.trim(),
          locale: values.locale,
          preferred_billing_currency: preferredBillingCurrency,
          referral_code: referralCode || undefined,
        });

        if (!envelope?.success || !envelope?.data?.access_token) {
          setErrorMessage(
            envelope?.error?.message || t("auth.register.error.generic")
          );
          setLoader(false);
          return;
        }

        // Mesmo storage do login — UI entra autenticada.
        persistTenantSession(envelope.data);
        dispatch(loginSuccess(envelope.data));

        // Preferência de idioma do formulário → painel.
        if (values.locale && values.locale !== i18n.language) {
          localStorage.setItem("I18N_LANGUAGE", values.locale);
          await i18n.changeLanguage(values.locale);
        }

        props.router.navigate(
          envelope.data.requires_checkout
            ? "/settings/subscription"
            : "/settings/sending",
          {
            replace: true,
            state: {
              fromRegister: true,
              requiresCheckout: !!envelope.data.requires_checkout,
            },
          }
        );
      } catch (err: any) {
        if (err?.status === 429 || err?.code === "RATE_LIMIT_EXCEEDED") {
          setErrorTone("warning");
        }
        setErrorMessage(resolveRegisterError(err, t));
        setLoader(false);
      }
    },
  });

  const handleGoogleCredential = async (idToken: string) => {
    if (!validation.values.accept_terms) {
      void validation.setFieldTouched("accept_terms", true);
      setErrorTone("danger");
      setErrorMessage(t("auth.register.validation.acceptTerms"));
      return;
    }
    setLoader(true);
    setErrorMessage(null);
    setErrorTone("danger");
    try {
      const envelope: any = await postJwtGoogleAuth({
        id_token: idToken,
        locale: validation.values.locale || defaultLocale,
        preferred_billing_currency: preferredBillingCurrency,
        referral_code: referralCode || undefined,
        company_name: validation.values.company_name?.trim() || undefined,
      });

      if (!envelope?.success || !envelope?.data?.access_token) {
        setErrorMessage(
          envelope?.error?.message || t("auth.register.error.generic")
        );
        setLoader(false);
        return;
      }

      persistTenantSession(envelope.data);
      dispatch(loginSuccess(envelope.data));

      if (
        validation.values.locale &&
        validation.values.locale !== i18n.language
      ) {
        localStorage.setItem("I18N_LANGUAGE", validation.values.locale);
        await i18n.changeLanguage(validation.values.locale);
      }

      if (envelope.data.created) {
        props.router.navigate("/onboarding/google", {
          replace: true,
          state: {
            fromRegister: true,
            requiresCheckout: !!envelope.data.requires_checkout,
          },
        });
        return;
      }

      props.router.navigate(
        envelope.data.requires_checkout
          ? "/settings/subscription"
          : "/settings/sending",
        {
          replace: true,
          state: {
            fromRegister: true,
            requiresCheckout: !!envelope.data.requires_checkout,
          },
        }
      );
    } catch (err: any) {
      if (err?.status === 429 || err?.code === "RATE_LIMIT_EXCEEDED") {
        setErrorTone("warning");
      }
      setErrorMessage(resolveRegisterError(err, t));
      setLoader(false);
    }
  };

  const setLocaleFromBar = (locale: ProductLocale) => {
    void validation.setFieldValue("locale", locale);
  };

  useEffect(() => {
    validation.setFormikState((prev) => ({
      ...prev,
      validationSchema: Yup.object({
        name: Yup.string().required(t("auth.register.validation.nameRequired")),
        email: Yup.string()
          .email(t("auth.register.validation.emailInvalid"))
          .required(t("auth.register.validation.emailRequired")),
        company_name: Yup.string().required(
          t("auth.register.validation.companyRequired")
        ),
        password: Yup.string()
          .min(8, t("auth.register.validation.passwordMin"))
          .required(t("auth.register.validation.passwordRequired")),
        confirm_password: Yup.string()
          .oneOf(
            [Yup.ref("password")],
            t("auth.register.validation.passwordMismatch")
          )
          .required(t("auth.register.validation.confirmRequired")),
        locale: Yup.string().oneOf([...LOCALES]),
        accept_terms: Yup.boolean().oneOf(
          [true],
          t("auth.register.validation.acceptTerms")
        ),
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  document.title = t("auth.register.documentTitle");

  const siteUrl = (
    process.env.REACT_APP_SITE_URL || "https://novo.brenvio.com.br"
  ).replace(/\/$/, "");

  const showTrialCopy = trialActive === true;
  const catalogLoading = trialActive === null;

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4">
                  <Link
                    to={authSiblingPath("/login")}
                    className="d-inline-block auth-logo"
                  >
                    <img
                      src={getBrandLogos().dark}
                      alt={getBrandLogos().alt}
                      height="20"
                    />
                  </Link>
                  <p className="mt-3 fs-15 fw-medium text-muted">
                    {t("auth.brandSubtitle")}
                  </p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <AuthLocaleBar
                      value={validation.values.locale}
                      onChange={setLocaleFromBar}
                    />

                    <div className="text-center mt-2">
                      <h5 className="text-primary">{t("auth.register.title")}</h5>
                      {catalogLoading ? (
                        <p
                          className="text-muted placeholder-glow mb-0"
                          aria-busy="true"
                          aria-live="polite"
                        >
                          <span className="placeholder col-10" />
                          <span className="visually-hidden">
                            {t("auth.register.subtitle.loading")}
                          </span>
                        </p>
                      ) : (
                        <p className="text-muted">
                          {showTrialCopy
                            ? t("auth.register.subtitle.trial")
                            : t("auth.register.subtitle.paid")}
                        </p>
                      )}
                      {intlMarket ? (
                        <Alert color="info" className="text-start mb-0 mt-3">
                          {catalogLoading
                            ? t("auth.register.intlBillingHint.loading")
                            : showTrialCopy
                              ? t("auth.register.intlBillingHint.trial")
                              : t("auth.register.intlBillingHint.paid")}
                        </Alert>
                      ) : null}
                    </div>

                    {errorMessage ? (
                      <Alert color={errorTone} className="mt-3" role="alert">
                        {errorMessage}
                      </Alert>
                    ) : null}

                    {referralCode ? (
                      <Alert color="info" className="mt-3 mb-0">
                        {t("auth.register.referralApplied")}
                      </Alert>
                    ) : null}

                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        noValidate
                      >
                        <Input
                          type="hidden"
                          name="locale"
                          value={validation.values.locale}
                          readOnly
                        />

                        <div className="mb-3">
                          <Label htmlFor="name" className="form-label">
                            {t("auth.register.name")}
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            type="text"
                            autoComplete="name"
                            placeholder={t("auth.register.namePlaceholder")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.name}
                            invalid={
                              !!(
                                validation.touched.name &&
                                validation.errors.name
                              )
                            }
                          />
                          {validation.touched.name && validation.errors.name ? (
                            <FormFeedback type="invalid">
                              {validation.errors.name}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            {t("auth.register.email")}
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            placeholder={t("auth.register.emailPlaceholder")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email}
                            invalid={
                              !!(
                                validation.touched.email &&
                                validation.errors.email
                              )
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {validation.errors.email}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="company_name" className="form-label">
                            {t("auth.register.company")}
                          </Label>
                          <Input
                            id="company_name"
                            name="company_name"
                            type="text"
                            autoComplete="organization"
                            placeholder={t("auth.register.companyPlaceholder")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.company_name}
                            invalid={
                              !!(
                                validation.touched.company_name &&
                                validation.errors.company_name
                              )
                            }
                          />
                          {validation.touched.company_name &&
                          validation.errors.company_name ? (
                            <FormFeedback type="invalid">
                              {validation.errors.company_name}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="password" className="form-label">
                            {t("auth.register.password")}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup">
                            <Input
                              id="password"
                              name="password"
                              type={passwordShow ? "text" : "password"}
                              autoComplete="new-password"
                              className="form-control pe-5"
                              placeholder={t(
                                "auth.register.passwordPlaceholder"
                              )}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password}
                              invalid={
                                !!(
                                  validation.touched.password &&
                                  validation.errors.password
                                )
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                              aria-label={t("auth.register.togglePassword")}
                            >
                              <i className="ri-eye-fill align-middle" />
                            </button>
                          </div>
                          <div className="form-text">
                            {t("auth.register.passwordHint")}
                          </div>
                        </div>

                        <div className="mb-3">
                          <Label
                            htmlFor="confirm_password"
                            className="form-label"
                          >
                            {t("auth.register.confirmPassword")}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup">
                            <Input
                              id="confirm_password"
                              name="confirm_password"
                              type={confirmShow ? "text" : "password"}
                              autoComplete="new-password"
                              className="form-control pe-5"
                              placeholder={t(
                                "auth.register.confirmPlaceholder"
                              )}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.confirm_password}
                              invalid={
                                !!(
                                  validation.touched.confirm_password &&
                                  validation.errors.confirm_password
                                )
                              }
                            />
                            {validation.touched.confirm_password &&
                            validation.errors.confirm_password ? (
                              <FormFeedback type="invalid">
                                {validation.errors.confirm_password}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setConfirmShow(!confirmShow)}
                              aria-label={t("auth.register.togglePassword")}
                            >
                              <i className="ri-eye-fill align-middle" />
                            </button>
                          </div>
                        </div>

                        <div className="mb-3 form-check">
                          <Input
                            id="accept_terms"
                            name="accept_terms"
                            type="checkbox"
                            className="form-check-input"
                            checked={validation.values.accept_terms}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.accept_terms &&
                                validation.errors.accept_terms
                              )
                            }
                          />
                          <Label
                            className="form-check-label fs-13"
                            htmlFor="accept_terms"
                          >
                            {t("auth.register.acceptTermsPrefix")}{" "}
                            <a
                              href={`${siteUrl}/termos/`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("auth.register.termsLink")}
                            </a>{" "}
                            {t("auth.register.acceptTermsMiddle")}{" "}
                            <a
                              href={`${siteUrl}/privacidade/`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("auth.register.privacyLink")}
                            </a>
                            .
                          </Label>
                          {validation.touched.accept_terms &&
                          validation.errors.accept_terms ? (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.accept_terms}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mt-4">
                          <Button
                            color="success"
                            className="w-100"
                            type="submit"
                            disabled={loader}
                          >
                            {loader ? (
                              <Spinner size="sm" className="me-2">
                                …
                              </Spinner>
                            ) : null}
                            {showTrialCopy
                              ? t("auth.register.submit.trial")
                              : t("auth.register.submit.paid")}
                          </Button>
                        </div>

                        <GoogleSignInButton
                          busy={loader}
                          enableOneTap
                          onCredential={handleGoogleCredential}
                        />
                      </Form>
                    </div>
                  </CardBody>
                </Card>

                <div className="mt-4 text-center">
                  <p className="mb-0">
                    {t("auth.register.haveAccount")}{" "}
                    <Link
                      to={authSiblingPath("/login")}
                      className="fw-semibold text-primary text-decoration-underline"
                    >
                      {t("auth.register.signIn")}
                    </Link>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Register);
